/**
 * Live AR through WebXR: the ARCore path for Android Chrome.
 *
 * Android Chrome exposes `immersive-ar` sessions backed by ARCore — OS-grade
 * tracking and real plane detection, vertical planes included — so where the
 * browser offers that, it beats running our own SLAM. The 8th Wall engine
 * stays as the path for browsers without WebXR AR, which above all means iOS
 * Safari; the engine itself only uses WebXR on headsets, never on phones.
 *
 * The robot is mirrored exactly as on the 8th Wall path: the same clones, the
 * same per-frame sync, the same surface-pose placement — ARCore hit poses
 * also carry the surface normal along +Y, so `placeMirrorOnSurface` transfers
 * verbatim.
 */

import * as THREE from "three";
import type { LiveArCallbacks, LiveArSession } from "./arLiveEngine";
import { createMirrorRoot, placeMirrorOnSurface, robotPartOf, syncMirror } from "./arLiveMirror";

/**
 * Marks overlay elements whose taps are UI, not placement.
 *
 * With dom-overlay, a touch anywhere on the overlay — the joint sheet
 * included — also reaches the XR session as a `select`, and dragging a joint
 * slider must not teleport the robot. The dom-overlay spec's answer is the
 * `beforexrselect` event: cancelling it keeps the touch a plain DOM touch.
 */
export const LIVE_AR_UI_ATTRIBUTE = "data-live-ar-ui";

/** Whether this browser can run Live AR through WebXR instead of 8th Wall. */
export async function isWebXrArSupported(): Promise<boolean> {
  const xr = navigator.xr;
  if (!xr) {
    return false;
  }
  try {
    return await xr.isSessionSupported("immersive-ar");
  } catch {
    // Permissions policy can make the probe itself throw; that is a "no".
    return false;
  }
}

/**
 * The session request: hit-test is what tap-to-place runs on, and dom-overlay
 * is what keeps the joint sheet and exit button on screen — AR with no way
 * out is worse than no session, so both are required, which is also how
 * Variant Launch's viewer wants them asked for.
 */
export function webXrSessionInit(overlayRoot: Element | null): XRSessionInit {
  // Plane detection is optional because the session must still start
  // without it; where it exists it is what rescues a tap on a bare wall,
  // whose feature-point hit test finds nothing.
  const optionalFeatures = ["plane-detection"];
  if (!overlayRoot) {
    return { requiredFeatures: ["hit-test"], optionalFeatures };
  }
  return {
    requiredFeatures: ["hit-test", "dom-overlay"],
    optionalFeatures,
    domOverlay: { root: overlayRoot },
  };
}

/** A detected plane reduced to what placing the robot on it needs. */
export interface SurfacePlane {
  /** Plane origin in the reference space. */
  position: { x: number; y: number; z: number };
  /** Plane orientation, +Y along its normal — the pose placement wants. */
  orientation: { x: number; y: number; z: number; w: number };
  /** Its extent, as plane-space (x, z) points. */
  polygon: { x: number; z: number }[];
}

/**
 * How far outside a plane's measured polygon a tap still counts, in metres.
 *
 * ARCore usually recognises a fraction of a bare wall — a strip near a
 * poster, the edge by a door frame — so a tap a hand's width outside the
 * patch is still a tap on that wall, and refusing it is what makes wall
 * placement feel broken on Android. Bounded so a tap into open space past
 * the end of a table does not silently land on the table's plane.
 */
export const PLANE_HIT_MARGIN_M = 0.35;

/**
 * Where a ray meets the nearest detected plane, if it does.
 *
 * The standard hit test only reports points inside a plane's polygon (and
 * feature points, which bare walls do not give). Intersecting the planes
 * ourselves, with a margin, catches the wall the phone has partly mapped.
 */
export function nearestPlaneHit(
  origin: { x: number; y: number; z: number },
  direction: { x: number; y: number; z: number },
  planes: SurfacePlane[],
  margin = PLANE_HIT_MARGIN_M
): { position: THREE.Vector3; orientation: THREE.Quaternion } | null {
  const rayOrigin = new THREE.Vector3(origin.x, origin.y, origin.z);
  const rayDirection = new THREE.Vector3(direction.x, direction.y, direction.z).normalize();
  let best: { position: THREE.Vector3; orientation: THREE.Quaternion; distance: number } | null = null;

  for (const plane of planes) {
    const planePosition = new THREE.Vector3(plane.position.x, plane.position.y, plane.position.z);
    const planeQuaternion = new THREE.Quaternion(
      plane.orientation.x,
      plane.orientation.y,
      plane.orientation.z,
      plane.orientation.w
    );
    const normal = new THREE.Vector3(0, 1, 0).applyQuaternion(planeQuaternion);
    const denominator = normal.dot(rayDirection);
    if (Math.abs(denominator) < 1e-6) {
      // Edge-on: the ray runs along the plane and never lands on it.
      continue;
    }
    const distance = normal.dot(planePosition.clone().sub(rayOrigin)) / denominator;
    if (distance <= 0 || (best !== null && distance >= best.distance)) {
      continue;
    }

    const point = rayOrigin.clone().addScaledVector(rayDirection, distance);
    // Into plane space, where the polygon lives on (x, z).
    const local = point.clone().sub(planePosition).applyQuaternion(planeQuaternion.clone().invert());
    if (!withinPolygon(local.x, local.z, plane.polygon, margin)) {
      continue;
    }
    best = { position: point, orientation: planeQuaternion, distance };
  }

  return best ? { position: best.position, orientation: best.orientation } : null;
}

/** Whether (x, z) is inside the polygon, or within `margin` of its bounds. */
function withinPolygon(x: number, z: number, polygon: { x: number; z: number }[], margin: number): boolean {
  if (polygon.length === 0) {
    return false;
  }
  // The polygon of a detected plane is convex and roughly rectangular, so
  // its bounding box plus a margin is as good as an exact test here and
  // cannot disagree about the wall the finger was pointing at.
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const point of polygon) {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minZ = Math.min(minZ, point.z);
    maxZ = Math.max(maxZ, point.z);
  }
  return x >= minX - margin && x <= maxX + margin && z >= minZ - margin && z <= maxZ + margin;
}

/**
 * The detected planes of this frame, in the reference space.
 *
 * Returns nothing where the runtime has no plane detection (the feature is
 * optional, and iOS's viewer does not offer it), which just leaves the
 * standard hit test as the only path.
 */
/**
 * The nearest mirrored part a tap's ray runs through, if any.
 *
 * The ray is the pose of the touch's target ray space, which points into
 * the room from the screen where the finger is, so this is "what did I just
 * put my finger on" answered against the robot rather than the room.
 */
export function raycastMirrorFromScreen(
  mirrorRoot: THREE.Object3D,
  camera: THREE.Camera,
  ndcX: number,
  ndcY: number
): THREE.Intersection | null {
  const raycaster = new THREE.Raycaster();
  // The XR camera's inverse projection is what unprojecting the tap needs,
  // and three only maintains it on cameras it renders through itself.
  camera.projectionMatrixInverse.copy(camera.projectionMatrix).invert();
  raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
  // The clones write their world matrix straight into `matrix` and never
  // let three recompose it, so the graph has to be forced current.
  mirrorRoot.updateMatrixWorld(true);
  return raycaster.intersectObject(mirrorRoot, true)[0] ?? null;
}

/**
 * How far off a part a tap may land and still mean it, in CSS pixels.
 *
 * A finger covers about this much of the screen, and a robot arm seen
 * across the room is a few pixels wide: demanding a hit on the exact
 * silhouette asks for aim nobody has. Kept below the width of a fingertip
 * so a tap clearly beside the robot still means "stand over there".
 */
export const TAP_TOLERANCE_PX = 36;

/**
 * The part under or near the finger.
 *
 * A single ray through the tap is tried first, so a solid hit always wins
 * and always picks the nearest part. Failing that, rays through a ring
 * around the tap fatten it to a fingertip — the nearest of whatever they
 * find is what the user was reaching for.
 */
export function pickMirrorPartNearScreen(
  mirrorRoot: THREE.Object3D,
  camera: THREE.Camera,
  clientX: number,
  clientY: number,
  width: number,
  height: number,
  tolerancePx = TAP_TOLERANCE_PX
): THREE.Intersection | null {
  const centre = screenToNdc(clientX, clientY, width, height);
  const direct = raycastMirrorFromScreen(mirrorRoot, camera, centre.x, centre.y);
  if (direct) {
    return direct;
  }

  let best: THREE.Intersection | null = null;
  const SAMPLES_PER_RING = 8;
  for (const radius of [tolerancePx / 2, tolerancePx]) {
    for (let step = 0; step < SAMPLES_PER_RING; step += 1) {
      const angle = (step / SAMPLES_PER_RING) * Math.PI * 2;
      const point = screenToNdc(
        clientX + Math.cos(angle) * radius,
        clientY + Math.sin(angle) * radius,
        width,
        height
      );
      const hit = raycastMirrorFromScreen(mirrorRoot, camera, point.x, point.y);
      if (hit && (best === null || hit.distance < best.distance)) {
        best = hit;
      }
    }
    // An inner-ring hit is closer to where the finger actually was, so it
    // is taken rather than widening the search further.
    if (best) {
      return best;
    }
  }
  return best;
}

/** Screen point to normalised device coordinates, as the raycaster wants. */
export function screenToNdc(
  clientX: number,
  clientY: number,
  width: number,
  height: number
): { x: number; y: number } {
  return {
    x: (clientX / width) * 2 - 1,
    y: -((clientY / height) * 2) + 1,
  };
}

export function raycastMirror(
  mirrorRoot: THREE.Object3D,
  ray: XRPose
): THREE.Intersection | null {
  const origin = new THREE.Vector3(
    ray.transform.position.x,
    ray.transform.position.y,
    ray.transform.position.z
  );
  const direction = new THREE.Vector3(0, 0, -1).applyQuaternion(
    new THREE.Quaternion(
      ray.transform.orientation.x,
      ray.transform.orientation.y,
      ray.transform.orientation.z,
      ray.transform.orientation.w
    )
  );
  const raycaster = new THREE.Raycaster(origin, direction.normalize());
  // The clones write their world matrix straight into `matrix` and never
  // let three recompose it, so the raycaster has to be told the graph is
  // current rather than updating it from position/quaternion itself.
  mirrorRoot.updateMatrixWorld(true);
  return raycaster.intersectObject(mirrorRoot, true)[0] ?? null;
}

export function framePlanes(frame: XRFrame, referenceSpace: XRReferenceSpace): SurfacePlane[] {
  const detected = (frame as XRFrame & { detectedPlanes?: Set<XRPlane> }).detectedPlanes;
  if (!detected) {
    return [];
  }
  const planes: SurfacePlane[] = [];
  for (const plane of detected) {
    const pose = frame.getPose(plane.planeSpace, referenceSpace);
    if (!pose) {
      continue;
    }
    planes.push({
      position: pose.transform.position,
      orientation: pose.transform.orientation,
      polygon: plane.polygon.map((point) => ({ x: point.x, z: point.z })),
    });
  }
  return planes;
}

/**
 * How much bigger the native framebuffer is than the "recommended" one.
 *
 * A layer at scale 1.0 gets the session's recommended resolution, which a
 * runtime may set well below the screen's for performance — Variant Launch's
 * iOS viewer does, and the robot comes out blurry over a sharp camera feed.
 * Asking for the native scale renders the robot at screen resolution; a
 * runtime that cannot say (or an emulated one that balks) just keeps 1.
 */
export function nativeFramebufferScale(session: XRSession): number {
  try {
    if (
      typeof XRWebGLLayer !== "undefined" &&
      typeof XRWebGLLayer.getNativeFramebufferScaleFactor === "function"
    ) {
      return XRWebGLLayer.getNativeFramebufferScaleFactor(session) || 1;
    }
  } catch {
    // A polyfilled layer class may reject a session it did not create.
  }
  return 1;
}

/**
 * The pose a tap should place the robot at.
 *
 * The transient-input results are the screen touch itself, so they win: the
 * robot lands on the surface under the finger. The viewer-space source only
 * backs them up — it looks through the centre of the screen, which is still a
 * real surface in the room rather than a tap that does nothing.
 */
export function bestHitPose(
  frame: XRFrame,
  referenceSpace: XRReferenceSpace,
  transientSource: XRTransientInputHitTestSource | null,
  viewerSource: XRHitTestSource | null
): XRPose | null {
  if (transientSource) {
    for (const perInput of frame.getHitTestResultsForTransientInput(transientSource)) {
      const pose = perInput.results[0]?.getPose(referenceSpace);
      if (pose) {
        return pose;
      }
    }
  }
  const pose = viewerSource ? frame.getHitTestResults(viewerSource)[0]?.getPose(referenceSpace) : undefined;
  return pose ?? null;
}

/**
 * Start an ARCore session and mirror the given scene's robot into the room.
 *
 * Same contract as the 8th Wall path: resolving means the session can be
 * stopped; the camera being visible arrives as `onCameraReady`, and a refused
 * or failed session as `onCameraError` rather than as a rejection.
 */
export async function startWebXrLiveAr(
  canvas: HTMLCanvasElement,
  getSourceScene: () => THREE.Object3D | null,
  callbacks: LiveArCallbacks = {},
  overlayRoot: HTMLElement | null = null
): Promise<LiveArSession> {
  let settled = false;
  const cameraReady = () => {
    if (!settled) {
      settled = true;
      callbacks.onCameraReady?.();
    }
  };
  const cameraFailed = (error: Error) => {
    if (!settled) {
      settled = true;
      callbacks.onCameraError?.(error);
    }
  };

  let session: XRSession;
  try {
    // navigator.xr exists here: isWebXrArSupported chose this path.
    session = await navigator.xr!.requestSession("immersive-ar", webXrSessionInit(overlayRoot));
  } catch (error) {
    // Almost always the camera permission being denied.
    cameraFailed(
      error instanceof Error && error.name !== "NotSupportedError" && error.message
        ? new Error(`The AR session could not be started: ${error.message}`)
        : new Error("The AR session could not be started. Check that this page may use the camera.")
    );
    return { stop: () => undefined };
  }

  const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
  renderer.xr.enabled = true;
  // 'local' rather than three's default 'local-floor': 'local' is granted to
  // every session, while 'local-floor' would have to be requested as a
  // feature. The floor is found by tapping it, not by the reference space.
  renderer.xr.setReferenceSpaceType("local");
  const framebufferScale = nativeFramebufferScale(session);
  if (framebufferScale > 1) {
    // Before setSession, which is when three builds the layer.
    renderer.xr.setFramebufferScaleFactor(framebufferScale);
  }
  // The XR layer is not always a separate framebuffer: polyfilled runtimes —
  // Variant Launch's viewer among them — render into the canvas's own
  // drawing buffer and ignore the scale factor above. A WebGLRenderer's
  // canvas starts at 300x150, so without this the robot is drawn at
  // thumbnail size and stretched over the screen: a blur over a sharp
  // camera feed. Rendering *denser* than the screen (1.5x) then keeps edges
  // crisp through the compositor's own smoothing — supersampling, cheap for
  // a robot-only scene. Real compositors ignore the canvas entirely, so
  // both are free on Chrome.
  renderer.setPixelRatio(Math.min(window.devicePixelRatio * 1.5, 4));
  // updateStyle false: sizing the *drawing buffer* is the point, and letting
  // three write a 100vh CSS height onto the canvas pushes the joint sheet
  // out of the overlay's flex column and off the bottom of the screen.
  renderer.setSize(window.innerWidth, window.innerHeight, false);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera();
  const mirrorRoot = createMirrorRoot();
  // Until the first tap: roughly on the floor a couple of steps ahead.
  // 'local' space starts at the device, which is held at chest height.
  mirrorRoot.position.set(0, -1.4, -2);
  scene.add(mirrorRoot);
  // The same light rig as the 8th Wall path, so the robot reads the same.
  scene.add(new THREE.AmbientLight(0xffffff, 0.9));
  const sun = new THREE.DirectionalLight(0xffffff, 1.2);
  sun.position.set(3, 10, 5);
  scene.add(sun);
  const clones = new Map<string, THREE.Mesh>();

  let transientSource: XRTransientInputHitTestSource | null = null;
  let viewerSource: XRHitTestSource | null = null;

  // Belt over beforexrselect's braces: an emulated dom-overlay (Variant
  // Launch's) may not fire beforexrselect at all, so the select handler is
  // parked for as long as a touch that started on the UI is down — however
  // long the joint drag lasts — and for a moment after it lifts, because
  // that is when the runtime delivers the select that would otherwise
  // teleport the robot to wherever the finger left the slider.
  let uiPointerDown = false;
  let uiPointerUntil = 0;
  // Where the finger last went down, which is what decides *which part* of
  // the robot a tap meant. Listened for on the window rather than on the
  // overlay: the overlay and its canvas are `pointer-events: none` so the
  // camera area generates no events of its own, but the window sees them
  // all the same.
  let lastPointer: { x: number; y: number; at: number } | null = null;
  const markUiPointer = (event: Event) => {
    const target = event.target;
    if (target instanceof Element && target.closest(`[${LIVE_AR_UI_ATTRIBUTE}]`)) {
      uiPointerDown = true;
      return;
    }
    if (event instanceof PointerEvent || event instanceof MouseEvent) {
      lastPointer = { x: event.clientX, y: event.clientY, at: performance.now() };
    } else if (typeof TouchEvent !== "undefined" && event instanceof TouchEvent) {
      const touch = event.touches[0] ?? event.changedTouches[0];
      if (touch) {
        lastPointer = { x: touch.clientX, y: touch.clientY, at: performance.now() };
      }
    }
  };
  const releaseUiPointer = () => {
    if (uiPointerDown) {
      uiPointerDown = false;
      uiPointerUntil = performance.now() + 300;
    }
  };

  const handleSelect = (event: XRInputSourceEvent) => {
    if (uiPointerDown || performance.now() < uiPointerUntil) {
      return;
    }
    const referenceSpace = renderer.xr.getReferenceSpace();
    if (!referenceSpace) {
      return;
    }

    // A tap that lands on the robot means that part of it, not the floor
    // behind it: it picks the joint to pose rather than moving the robot,
    // which is also what stops a tap on the robot teleporting it onto
    // itself. Tapping the room over there still moves it.
    //
    // Two ways to aim, because the XR input ray is not always there: the
    // finger's own screen position through the XR camera first, since that
    // is exactly where the user looked, then the input source's target ray.
    const tapRay = event.frame.getPose(event.inputSource.targetRaySpace, referenceSpace);
    const fresh = lastPointer !== null && performance.now() - lastPointer.at < 1500;
    let hit: THREE.Intersection | null = null;
    if (lastPointer && fresh) {
      hit = pickMirrorPartNearScreen(
        mirrorRoot,
        renderer.xr.getCamera(),
        lastPointer.x,
        lastPointer.y,
        window.innerWidth,
        window.innerHeight
      );
    }
    if (!hit && tapRay) {
      hit = raycastMirror(mirrorRoot, tapRay);
    }
    callbacks.onTapDiagnostics?.({
      hadPointer: Boolean(lastPointer && fresh),
      hadXrRay: Boolean(tapRay),
      mirroredParts: mirrorRoot.children.length,
      part: hit ? robotPartOf(hit.object) : null,
    });
    if (hit && callbacks.onRobotPartTapped) {
      callbacks.onRobotPartTapped(robotPartOf(hit.object));
      return;
    }

    const pose = bestHitPose(event.frame, referenceSpace, transientSource, viewerSource);
    if (pose) {
      // ARCore surface poses have +Y along the normal, exactly like the 8th
      // Wall ones — floors, walls and ramps all fall out of this transform.
      placeMirrorOnSurface(mirrorRoot, pose.transform.position, pose.transform.orientation);
      return;
    }
    // Nothing under the finger: a bare wall gives ARCore no feature points
    // and it maps only part of the surface, so the hit test comes back
    // empty a hand's width outside the patch it did map. Aim the finger's
    // own ray at the planes it has, generously.
    const ray = event.frame.getPose(event.inputSource.targetRaySpace, referenceSpace);
    if (!ray) {
      return;
    }
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(
      new THREE.Quaternion(
        ray.transform.orientation.x,
        ray.transform.orientation.y,
        ray.transform.orientation.z,
        ray.transform.orientation.w
      )
    );
    const planeHit = nearestPlaneHit(
      ray.transform.position,
      forward,
      framePlanes(event.frame, referenceSpace)
    );
    if (planeHit) {
      placeMirrorOnSurface(mirrorRoot, planeHit.position, planeHit.orientation);
    }
  };

  const suppressUiSelect = (event: Event) => {
    const target = event.target;
    if (target instanceof Element && target.closest(`[${LIVE_AR_UI_ATTRIBUTE}]`)) {
      event.preventDefault();
    }
  };

  let cleaned = false;
  const cleanUp = () => {
    if (cleaned) {
      return;
    }
    cleaned = true;
    overlayRoot?.removeEventListener("beforexrselect", suppressUiSelect);
    window.removeEventListener("pointerdown", markUiPointer, true);
    window.removeEventListener("touchstart", markUiPointer, true);
    window.removeEventListener("pointerup", releaseUiPointer, true);
    window.removeEventListener("pointercancel", releaseUiPointer, true);
    renderer.setAnimationLoop(null);
    transientSource?.cancel();
    viewerSource?.cancel();
    clones.clear();
    renderer.dispose();
    // Keep teardown identical to the 8th Wall path, where the canvas has to
    // be removed by hand; here React would unmount it anyway.
    canvas.remove();
  };

  try {
    await renderer.xr.setSession(session);
  } catch (error) {
    cameraFailed(error instanceof Error ? error : new Error(String(error)));
    session.end().catch(() => undefined);
    cleanUp();
    return { stop: () => undefined };
  }

  // Hit-test sources arrive after the session is up. The transient-input one
  // can be missing (it is its own optional API), and losing it degrades taps
  // to centre-screen placement, not to nothing.
  try {
    transientSource =
      (await session.requestHitTestSourceForTransientInput?.({ profile: "generic-touchscreen" })) ?? null;
  } catch {
    transientSource = null;
  }
  try {
    const viewerSpace = await session.requestReferenceSpace("viewer");
    viewerSource = (await session.requestHitTestSource?.({ space: viewerSpace })) ?? null;
  } catch {
    viewerSource = null;
  }

  session.addEventListener("select", handleSelect);
  // The session can end from outside `stop` — Android's back gesture, the
  // system UI — and the renderer must not keep an ended session's loop.
  session.addEventListener("end", cleanUp);
  overlayRoot?.addEventListener("beforexrselect", suppressUiSelect);
  // On the window: the overlay and its canvas take no pointer events, so a
  // tap on the camera view never reaches them — but the window sees it.
  // touchstart as well, because a runtime that synthesises no pointer
  // events still sends touches.
  window.addEventListener("pointerdown", markUiPointer, true);
  window.addEventListener("touchstart", markUiPointer, true);
  window.addEventListener("pointerup", releaseUiPointer, true);
  window.addEventListener("pointercancel", releaseUiPointer, true);

  renderer.setAnimationLoop(() => {
    // Presenting frames means the camera is on screen: WebXR has no separate
    // camera status, the feed is composited behind the transparent canvas.
    cameraReady();
    const source = getSourceScene();
    if (source) {
      syncMirror(source, mirrorRoot, clones);
    }
    renderer.render(scene, camera);
  });

  return {
    stop: () => {
      // Ending an already-ended session rejects; that is not news.
      session.end().catch(() => undefined);
      cleanUp();
    },
  };
}
